"use strict";

import { useEffect, useRef, useState } from "react";

import { getQATimeConfig, sendQAHeartbeat } from "./api";

export const HEARTBEAT_INTERVAL_MS = 60 * 1000;
export const ACTIVE_WINDOW_MS = 3 * 60 * 1000;

const INPUT_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart", "scroll"];
// HTMLMediaElement.HAVE_FUTURE_DATA: playing, not stalled for data.
const HAVE_FUTURE_DATA = 3;

// Any <video> on the page actually playing (not paused, ended or buffering).
export const isVideoPlaying = (root = document) =>
    Array.from(root.querySelectorAll("video")).some(
        (video) => !video.paused && !video.ended && video.readyState >= HAVE_FUTURE_DATA
    );

// Presence only: no keystrokes, text or positions ever leave the page.
export const heartbeatState = ({ visible, videoPlaying, lastInputAt, now }) => ({
    playing: Boolean(visible && videoPlaying),
    active: Boolean(visible && now - lastInputAt <= ACTIVE_WINDOW_MS),
});

/**
 * While the QA Review page is open for an in-progress review, sends a heartbeat
 * about every minute when the reviewer is watching the video or has interacted
 * in the last 3 minutes. Does nothing when the backend has time tracking off.
 */
export const useQATimeHeartbeat = ({ projectId, language, enabled }) => {
    const [trackingOn, setTrackingOn] = useState(false);
    const lastInputAt = useRef(Date.now());

    useEffect(() => {
        let cancelled = false;
        setTrackingOn(false);
        if (!enabled || !projectId || !language) return undefined;
        getQATimeConfig().then((on) => {
            if (!cancelled) setTrackingOn(on);
        });
        return () => {
            cancelled = true;
        };
    }, [enabled, projectId, language]);

    useEffect(() => {
        if (!trackingOn) return undefined;

        const markInput = () => {
            lastInputAt.current = Date.now();
        };
        INPUT_EVENTS.forEach((name) =>
            document.addEventListener(name, markInput, { capture: true, passive: true })
        );

        const timer = setInterval(() => {
            const { playing, active } = heartbeatState({
                visible: document.visibilityState === "visible",
                videoPlaying: isVideoPlaying(),
                lastInputAt: lastInputAt.current,
                now: Date.now(),
            });
            if (playing || active) {
                sendQAHeartbeat({ projectId, language, playing, active }).catch(() => {});
            }
        }, HEARTBEAT_INTERVAL_MS);

        return () => {
            clearInterval(timer);
            INPUT_EVENTS.forEach((name) =>
                document.removeEventListener(name, markInput, { capture: true })
            );
        };
    }, [trackingOn, projectId, language]);

    return trackingOn;
};
